import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, ChevronDown, Check, Plus, Trash2 } from 'lucide-react';

const DAYS = [
  { id: 'monday', label: 'Mon', full: 'Monday' },
  { id: 'tuesday', label: 'Tue', full: 'Tuesday' },
  { id: 'wednesday', label: 'Wed', full: 'Wednesday' },
  { id: 'thursday', label: 'Thu', full: 'Thursday' },
  { id: 'friday', label: 'Fri', full: 'Friday' },
  { id: 'saturday', label: 'Sat', full: 'Saturday' },
  { id: 'sunday', label: 'Sun', full: 'Sunday' }
];

const TIME_SLOTS = [];
for (let hour = 6; hour <= 23; hour++) {
  const h = hour.toString().padStart(2, '0');
  TIME_SLOTS.push({ value: `${h}:00`, label: `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}` });
  TIME_SLOTS.push({ value: `${h}:30`, label: `${hour > 12 ? hour - 12 : hour}:30 ${hour >= 12 ? 'PM' : 'AM'}` });
}

const SLOT_DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' }
];

const QUICK_PRESETS = [
  { id: 'weekdays', label: 'Weekdays', days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] },
  { id: 'weekends', label: 'Weekends', days: ['saturday', 'sunday'] },
  { id: 'all', label: 'All Days', days: DAYS.map(d => d.id) }
];

/**
 * LiteAvailabilityScheduler - Mobile-friendly availability picker
 * Supports multiple time ranges per day, services and rentals
 */
const LiteAvailabilityScheduler = ({ 
  value, 
  onChange, 
  listingType = 'service',
  showDuration = true 
}) => {
  const isInitialized = useRef(false);
  
  const [selectedDays, setSelectedDays] = useState(() => {
    if (value?.business_hours?.length > 0) {
      return value.business_hours.filter(bh => bh.enabled).map(bh => bh.day.toLowerCase());
    }
    return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  });
  
  // Support multiple time ranges
  const [timeRanges, setTimeRanges] = useState(() => {
    if (value?.business_hours?.length > 0) {
      const firstEnabled = value.business_hours.find(bh => bh.enabled);
      const ranges = firstEnabled?.time_ranges;
      if (ranges && ranges.length > 0) return ranges;
      return [{ from: firstEnabled?.from_time || '09:00', to: firstEnabled?.to_time || '18:00' }];
    }
    return [{ from: '09:00', to: '18:00' }];
  });
  
  const [slotDuration, setSlotDuration] = useState(value?.slot_duration || 60);
  const [concurrentSlots, setConcurrentSlots] = useState(value?.bookings_per_slot || 1);

  const stableOnChange = useCallback((data) => {
    onChange(data);
  }, [onChange]);

  useEffect(() => {
    const businessHours = DAYS.map(day => ({
      day: day.full,
      enabled: selectedDays.includes(day.id),
      from_time: timeRanges[0]?.from || '09:00',
      to_time: timeRanges[timeRanges.length - 1]?.to || '18:00',
      time_ranges: timeRanges,
    }));

    stableOnChange({
      business_hours: businessHours,
      slot_duration: slotDuration,
      bookings_per_slot: concurrentSlots,
      buffer_between_bookings: 0
    });
  }, [selectedDays, timeRanges, slotDuration, concurrentSlots, stableOnChange]);

  const toggleDay = (dayId) => {
    setSelectedDays(prev => 
      prev.includes(dayId) ? prev.filter(d => d !== dayId) : [...prev, dayId]
    );
  };

  const addTimeRange = () => {
    const lastTo = timeRanges[timeRanges.length - 1]?.to || '18:00';
    // Start the new range 30 min after last end
    const [h, m] = lastTo.split(':').map(Number);
    const newFrom = `${String(Math.min(h + (m >= 30 ? 1 : 0), 23)).padStart(2, '0')}:${m >= 30 ? '00' : '30'}`;
    const newTo = `${String(Math.min(h + 2, 23)).padStart(2, '0')}:${lastTo.split(':')[1]}`;
    setTimeRanges(prev => [...prev, { from: newFrom, to: newTo }]);
  };

  const removeTimeRange = (idx) => {
    if (timeRanges.length <= 1) return;
    setTimeRanges(prev => prev.filter((_, i) => i !== idx));
  };

  const updateTimeRange = (idx, field, val) => {
    setTimeRanges(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  const isService = listingType === 'service';

  const timeLabel = (ranges) => {
    return ranges.map(r => `${r.from}-${r.to}`).join(', ');
  };

  return (
    <div className="space-y-4">
      {/* Quick Presets */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Quick Select</label>
        <div className="flex gap-2">
          {QUICK_PRESETS.map(preset => {
            const isActive = preset.days.every(d => selectedDays.includes(d)) && 
                            selectedDays.length === preset.days.length;
            return (
              <button key={preset.id} type="button" onClick={() => setSelectedDays(preset.days)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Available Days <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-7 gap-1">
          {DAYS.map(day => (
            <button key={day.id} type="button" onClick={() => toggleDay(day.id)}
              className={`py-3 rounded-lg text-xs font-medium transition-all ${
                selectedDays.includes(day.id)
                  ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {day.label}
            </button>
          ))}
        </div>
        {selectedDays.length === 0 && (
          <p className="text-xs text-red-500 mt-1">Please select at least one day</p>
        )}
      </div>

      {/* Working Hours - Multiple Ranges */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Clock size={14} className="inline mr-1" />
          Working Hours
        </label>
        <div className="space-y-2">
          {timeRanges.map((range, idx) => (
            <div key={range.id || range.name || `${range.title || "range"}-${idx}`} className="flex items-center gap-2">
              <div className="relative flex-1">
                <select value={range.from} onChange={(e) => updateTimeRange(idx, 'from', e.target.value)}
                  data-testid={`time-from-${idx}`}
                  className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {TIME_SLOTS.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              <span className="text-gray-500 font-medium text-sm">to</span>
              <div className="relative flex-1">
                <select value={range.to} onChange={(e) => updateTimeRange(idx, 'to', e.target.value)}
                  data-testid={`time-to-${idx}`}
                  className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {TIME_SLOTS.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              {timeRanges.length > 1 && (
                <button type="button" onClick={() => removeTimeRange(idx)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 shrink-0">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addTimeRange}
            data-testid="add-time-range-btn"
            className="w-full py-2 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 flex items-center justify-center gap-1">
            <Plus size={14} /> Add Time Range
          </button>
        </div>
      </div>

      {/* Slot Duration (for services) */}
      {isService && showDuration && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Slot Duration</label>
          <div className="relative">
            <select value={slotDuration} onChange={(e) => setSlotDuration(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500">
              {SLOT_DURATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}

      {/* Concurrent Bookings (for services) */}
      {isService && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bookings per Slot</label>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setConcurrentSlots(Math.max(1, concurrentSlots - 1))}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200">-</button>
            <span className="text-xl font-semibold w-8 text-center">{concurrentSlots}</span>
            <button type="button" onClick={() => setConcurrentSlots(Math.min(10, concurrentSlots + 1))}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200">+</button>
            <span className="text-sm text-gray-500 ml-2">
              {concurrentSlots === 1 ? 'One-on-one' : `Up to ${concurrentSlots} at a time`}
            </span>
          </div>
        </div>
      )}

      {/* Summary */}
      {selectedDays.length > 0 && (
        <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100">
          <p className="text-sm text-indigo-800">
            <Check size={14} className="inline mr-1 text-indigo-600" />
            <strong>Available:</strong> {selectedDays.length} days/week, {timeRanges.length} time range{timeRanges.length > 1 ? 's' : ''}
            {isService && ` | ${slotDuration} min slots`}
          </p>
        </div>
      )}
    </div>
  );
};

export default LiteAvailabilityScheduler;
